import { useState, useEffect, useCallback } from 'react';

// protected-asset-indicator slug: fetches the set of access-protected
// resources (nodes / stations / networks that have a chat-user access
// list) so the UI can paint a lock badge. Read-only overlay sourced from
// aveq-agent's bulk /admin/access/protected via the aveq-admin proxy
// (/api/user-access/protected), which is read_nodes-gated — any operator
// who can see the topology sees the locks.
//
// Returns { isProtected(level, id), refresh, loading, error }.
// `isProtected` compares stringly: the endpoint returns resource_id as a
// string (node ids are 19-digit bigints; a JSON number would lose
// precision), and callers pass either string or numeric ids.
export function useProtectedResources() {
  const [set, setSet] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const r = await fetch('/api/user-access/protected', { credentials: 'same-origin' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const body = await r.json();
      const s = new Set((body.protected || []).map((p) => `${p.level}:${p.resource_id}`));
      setSet(s);
      setError(null);
    } catch (e) {
      // Soft-fail: no overlay rather than a broken page. An operator
      // lacking read_nodes (403) simply sees no locks.
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const isProtected = useCallback(
    (level, id) => set.has(`${level}:${String(id)}`),
    [set],
  );

  return { isProtected, refresh, loading, error };
}
